/**
 * Predicates over API rejections, and the error type they run against.
 *
 * Deliberately its OWN module, with no imports: apiClient touches `window` via
 * MSAL at module load, so anything that needs testing without a browser
 * environment cannot live in there.
 *
 * ⚠️ THE SHAPE THAT ACTUALLY ARRIVES. apiClient's response interceptor replaces
 * every Axios error with a new error before any caller sees it, so `err.response`
 * is GONE by then. A predicate written against `err.response.status` therefore
 * matches nothing in production while passing any test that hand-builds an Axios
 * shape — which is exactly what happened to `isNotFoundError`: the idempotent
 * attachment DELETE from review #301 never once took its 404 path in prod.
 *
 * So the interceptor now throws `ApiError`, carrying `status`, and these
 * predicates read that first. They still accept the raw Axios shape as a
 * fallback, for a caller that ever bypasses the interceptor.
 */

/** Error thrown by every apiClient call. `status` is absent for network faults. */
export class ApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * What the request interceptor throws, with no status, when a signed-in person's token cannot be renewed. It is actionable (only a refresh helps),
 * unlike the other status-less failures ("Network Error"), so a caller that shows its own words for an ordinary failure can show this one as it is.
 */
export const SESSION_EXPIRED_MESSAGE = 'Your sign-in session has expired — refresh the page to sign in again, then retry.';

/** The HTTP status behind a rejection, from either shape. */
export function statusOf(err: unknown): number | undefined {
  const e = err as { status?: unknown; response?: { status?: unknown } } | null | undefined;
  const direct = e?.status;
  if (typeof direct === 'number') return direct;
  const nested = e?.response?.status;
  return typeof nested === 'number' ? nested : undefined;
}

/**
 * True for an API rejection carrying HTTP 404.
 *
 * Used by `attachmentsApi.remove` to resolve an already-gone delete instead of
 * rejecting. Getting the shape wrong fails CLOSED — the 404 rethrows and the
 * error banner the normalization exists to prevent comes back. (An earlier note
 * here claimed it fails OPEN, swallowing every error; that was backwards, and
 * the closed failure is what shipped.)
 */
export const isNotFoundError = (err: unknown): boolean => statusOf(err) === 404;

/**
 * True for an API rejection carrying HTTP 409.
 *
 * The phase-write optimistic-concurrency guard (#379) answers a stale write with
 * a conflict, and the accept-mob-end action turns that into "someone else
 * changed this" rather than an error. Getting the shape wrong fails CLOSED here
 * too: the conflict escapes an async click handler as an unhandled rejection.
 */
export const isConflictError = (err: unknown): boolean => statusOf(err) === 409;

/**
 * Normalize any thrown value into an `ApiError`, preserving the HTTP status.
 *
 * Extracted from the interceptor so it can be tested at all — the interceptor
 * itself lives in a module that cannot be imported without a browser.
 */
export function toApiError(err: unknown): ApiError {
  const e = err as {
    response?: { data?: { message?: unknown; error?: unknown } };
    message?: unknown;
  } | null | undefined;
  const raw = e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message
    ?? 'An unknown error occurred';
  const message = Array.isArray(raw) ? raw.join(', ') : String(raw);
  return new ApiError(message, statusOf(err));
}

/**
 * True when the API definitely REFUSED the request, so nothing was saved and
 * it is safe to change the request and send it again as a new one.
 *
 * A 4xx is a decision the server made about the request itself. Except:
 *  - 409, which here means "this conflicts with something already saved" —
 *    for a retried post, the earlier attempt's post;
 *  - 408 and 429, which say "not now", not "not this".
 * No status at all (a dropped connection, a timeout) and 5xx are AMBIGUOUS:
 * the write may have happened. Used by the site photo composer to decide
 * whether a failed post attempt stays frozen (review of #560).
 */
export function isDefiniteRejection(err: unknown): boolean {
  const s = statusOf(err);
  return s !== undefined && s >= 400 && s < 500 && s !== 408 && s !== 409 && s !== 429;
}
